import * as Sentry from '@sentry/browser';
import log from './log';

/**
 * `'unknown'` is for acquisitions made from inside sip.js by the media factory, which sees the
 * constraints but not whether the session it is building is an INVITE or a 200 OK.
 */
export type MediaCallDirection = 'incoming' | 'outgoing' | 'unknown';

export interface MediaAcquisitionContext {
  direction: MediaCallDirection;
  sessionId?: string | null;
  deviceId?: string | null;
  label?: string | null;
  target?: string | number | null;
  /** The failure came from the pre-call probe rather than from the SIP session itself. */
  probe?: boolean;
  /** Another constraint was tried afterwards and opened, so the call was NOT lost. */
  recovered?: boolean;
  /** The constraint OverconstrainedError blamed, e.g. 'deviceId'. */
  constraint?: string | null;
  /** 0-based rung of the fallback ladder this failure came from. */
  attempt?: number;
}

/**
 * @name reportMediaAcquisitionError
 * @description Logs and reports to Sentry a getUserMedia/local-media failure raised while
 * acquiring the microphone during a SIP `.invite()` (outgoing) or `.accept()` (incoming).
 * These rejections are otherwise fire-and-forget and reach Sentry, if at all, as anonymous
 * unhandled rejections with no context. Tagging by `direction` and `errorName`
 * (e.g. NotReadableError, OverconstrainedError, NotAllowedError) lets support classify
 * "dead microphone" / one-way-call reports from the field.
 *
 * `ctx.recovered` separates the two very different things this used to conflate. A pre-call probe
 * that failed on the preferred device and then opened the system default is worth knowing about -
 * the user's chosen microphone is unusable - but the call went through, so it is reported as a
 * warning under its own fingerprint instead of drowning the real, call-losing failures.
 */
export function reportMediaAcquisitionError(
  err: unknown,
  ctx: MediaAcquisitionContext
): void {
  const errorName = (err as Error)?.name ?? 'Unknown';

  if (ctx.recovered) {
    log.warn(
      `NUACOM-LOG: the preferred microphone could not be opened for an ${ctx.direction} call (${errorName}), falling back to the system default.`,
      err
    );

    Sentry.captureException(err, {
      level: 'warning' as any,
      tags: {
        feature: 'webrtc-getusermedia',
        direction: ctx.direction,
        errorName,
        recovered: 'true',
      },
      contexts: { call: { ...ctx } },
      fingerprint: ['webrtc-getusermedia-recovered', ctx.direction, errorName],
    });

    return;
  }

  log.error(
    `NUACOM-LOG: getUserMedia failed on ${ctx.direction} call (${errorName}).`,
    err
  );

  Sentry.captureException(err, {
    tags: {
      feature: 'webrtc-getusermedia',
      direction: ctx.direction,
      errorName,
    },
    contexts: { call: { ...ctx } },
    fingerprint: ['webrtc-getusermedia', ctx.direction, errorName],
  });
}

export interface LocalAudioStats {
  deviceLabel?: string | null;
  deviceId?: string | null;
  trackMuted?: boolean;
  trackEnabled?: boolean;
  trackReadyState?: string;
  audioLevel?: number | null;
  totalAudioEnergy?: number | null;
  totalSamplesDuration?: number | null;
  bytesSent?: number | null;
  packetsSent?: number | null;
}

export interface MicrophoneHealthContext {
  direction: MediaCallDirection;
  sessionId?: string | null;
}

/**
 * @name reportMicrophoneHealth
 * @description Evaluates a post-establish snapshot of the local microphone audio stats and, when
 * the mic looks dead (access was granted but no audio is flowing), reports a warning to Sentry.
 * This targets the "sends some bytes then nothing" / one-way-call symptom where `getUserMedia`
 * succeeded but the source produces silence.
 *
 * `totalAudioEnergy ~ 0` alone is NOT conclusive (a working mic in a quiet room with noise
 * suppression can also read near-zero), so the dead-signal is the combination of: muted track,
 * non-live track, no samples flowing, or zero energy while samples flow. The full snapshot is
 * always attached as a breadcrumb so it is visible on any later event; a Sentry event is only
 * raised for suspicious snapshots to keep quota usage low.
 */
export function reportMicrophoneHealth(
  stats: LocalAudioStats,
  ctx: MicrophoneHealthContext
): void {
  const reasons: string[] = [];

  if (stats.trackMuted === true) reasons.push('track-muted');
  if (stats.trackReadyState !== 'live') reasons.push('track-not-live');
  if (!stats.totalSamplesDuration) reasons.push('no-samples');
  else if (stats.totalAudioEnergy === 0) reasons.push('zero-energy');

  const looksDead = reasons.length > 0;

  // Always attach the snapshot as a breadcrumb (cheap; surfaces if any later event fires).
  Sentry.addBreadcrumb({
    category: 'webrtc-mic-health',
    level: (looksDead ? 'warning' : 'info') as any,
    message: `mic health (${ctx.direction})`,
    data: { ...stats, ...ctx },
  });

  log.warn(
    `NUACOM-LOG: microphone produced no audio 10s after establish (${reasons.join(
      ','
    )}).`,
    stats
  );

  if (!looksDead) {
    return;
  }

  Sentry.captureMessage(
    `Microphone produced no audio 10s after establish (${ctx.direction})`,
    {
      level: 'warning' as any,
      tags: {
        feature: 'webrtc-getusermedia',
        check: 'post-establish-audio',
        direction: ctx.direction,
        deadReason: reasons.join(','),
      },
      contexts: { microphone: { ...stats, ...ctx } },
      fingerprint: ['webrtc-mic-health', ctx.direction],
    }
  );
}

export default reportMediaAcquisitionError;
